/**
 * The illustrated card — pure geometry and formatting (REDESIGN §7c).
 *
 * `CardVisual.tsx` draws it; everything here is testable without a DOM: the 1.586 aspect, the
 * three sizes, the last-four extraction (Financy hands us `accountNumber` as a masked PAN like
 * `458096xxxxxx8993`, but our own API and the specs use `***6044`) and the mask string.
 *
 * Never store or render more than four digits: `lastFour` deliberately throws away everything
 * else, so a full PAN cannot reach the DOM through this component (ARCHITECTURE §0.6).
 */

/** ISO 7810 ID-1 aspect ratio, the shape of a real card (REDESIGN §7c). */
export const CARD_ASPECT = 1.586;

/** Card size → rendered width in CSS pixels. */
export const CARD_SIZES = { sm: 176, md: 240, lg: 320 } as const;
export type CardSize = keyof typeof CARD_SIZES;

/** The network marks we draw as simple shapes. Anything else renders no mark. */
export type CardNetwork = 'visa' | 'mastercard' | 'other';

/** What the card is: live, blocked by the issuer, or closed (`creditStatus` deleted). */
export type CardState = 'active' | 'blocked' | 'closed';

/** The width and height, in CSS pixels, of a card at `size`. Height is rounded to 0.1px. */
export function cardBox(size: CardSize): { width: number; height: number } {
  const width = CARD_SIZES[size];
  return { width, height: Math.round((width / CARD_ASPECT) * 10) / 10 };
}

/**
 * The last four digits of any masked or unmasked card number we are handed.
 *
 * Accepts `458096xxxxxx8993`, `***6044`, `**** 8993`, `8993` and returns `'8993'`; returns null
 * for null/undefined/empty input or anything with fewer than four digits, so the caller renders
 * the card without a number rather than a wrong one.
 */
export function lastFour(cardNumber: string | null | undefined): string | null {
  if (typeof cardNumber !== 'string') return null;
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return digits.slice(-4);
}

/**
 * The digits row printed on the card: `groups - 1` bullet quads then the last four, e.g.
 * `•••• •••• •••• 8993`. `groups` is clamped to 1…4. Returns `null` when there is nothing to
 * print, and never contains a real digit other than the last four.
 */
export function formatCardMask(last4: string | null | undefined, groups = 4): string | null {
  if (typeof last4 !== 'string' || !/^\d{4}$/.test(last4)) return null;
  const n = Math.min(4, Math.max(1, Math.trunc(groups)));
  return [...Array<string>(n - 1).fill('••••'), last4].join(' ');
}

/**
 * The network implied by a PAN's first digit (4 → Visa, 5 → Mastercard). Returns null for a
 * masked number that starts with `*`/`•`, for Amex/Diners/other ranges and for empty input —
 * the caller then draws no network mark rather than the wrong one.
 */
export function networkFromPan(cardNumber: string | null | undefined): CardNetwork | null {
  if (typeof cardNumber !== 'string') return null;
  const first = cardNumber.trim().charAt(0);
  if (first === '4') return 'visa';
  if (first === '5') return 'mastercard';
  return null;
}
